import { useEffect, useState } from 'react'
import { Button, Field, HStack, Input, NativeSelect, Switch, Text, VStack } from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { useImpactContests, useImpactSettings, useSetImpactSettings } from '../../../hooks/useCampaign'
import { extractApiMessage } from '../../../lib/apiError'
import { OFFICE_LABEL, contestKey, formatVotes, parseContestKey } from '../../../lib/impact'
import { Card } from '../../shared'

const MIN_RATE = 5
const MAX_RATE = 100

/**
 * [Canvasser redesign Phase 8 / P8-5, P8-9] What canvassers see on their impact card: the votes estimate
 * (on/off + rate) and an optional past statewide race to compare against. Saved through its own endpoint so
 * it never clobbers other campaign fields. Client checks are pre-flight only; the server is the gate.
 * The race picker is NOT inside Field.Root (Chakra v3 Field.Root overrides NativeSelect `disabled`).
 */
export function ImpactSettingsEditor({ campaignId }: { campaignId: string }) {
  const { data, isLoading, error: loadError } = useImpactSettings(campaignId)
  const contests = useImpactContests(campaignId)
  const save = useSetImpactSettings(campaignId)
  // A failed GET locks the form: saving would overwrite settings the organizer never saw.
  const locked = isLoading || !!loadError
  const [votesEnabled, setVotesEnabled] = useState(true)
  // Empty until the GET lands, so a shown rate is always the saved one (never a placeholder default).
  const [rate, setRate] = useState('')
  const [contest, setContest] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setVotesEnabled(data.votesEnabled)
    setRate(String(data.conversationsPerVote))
    setContest(data.contestOffice && data.contestYear ? contestKey(data.contestOffice, data.contestYear) : '')
  }, [data])

  const list = contests.data ?? []
  const noContests = contests.isSuccess && list.length === 0

  const onSave = async () => {
    if (locked) return
    const perVote = Number(rate)
    if (!Number.isInteger(perVote) || perVote < MIN_RATE || perVote > MAX_RATE) {
      setFormError('Enter a number from 5 to 100.')
      return
    }
    const picked = parseContestKey(contest)
    setFormError(null)
    try {
      await save.mutateAsync({
        votesEnabled,
        conversationsPerVote: perVote,
        contestOffice: picked?.office ?? null,
        contestYear: picked?.year ?? null,
      })
      toaster.create({ type: 'success', title: 'Impact settings saved' })
    } catch (err) {
      setFormError(extractApiMessage(err, 'Failed to save the impact settings.'))
    }
  }

  return (
    <Card>
      <VStack align="stretch" gap={3}>
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">Impact estimates for canvassers</Text>
          <Text fontSize="sm" color="muted">
            Canvassers see their conversations, the team's, and how much of your turf has been reached. You can
            also show an estimate of extra votes and compare against a past race.
          </Text>
        </VStack>
        <Switch.Root
          checked={votesEnabled}
          onCheckedChange={(e) => setVotesEnabled(e.checked)}
          disabled={locked}
          minH="44px"
        >
          <Switch.HiddenInput />
          <Switch.Control />
          <Switch.Label>Show an estimate of extra votes</Switch.Label>
        </Switch.Root>
        <Field.Root maxW="16rem">
          <Field.Label>Conversations per extra vote</Field.Label>
          <Input
            type="number" inputMode="numeric" min={MIN_RATE} max={MAX_RATE} minH="44px"
            value={rate} onChange={e => setRate(e.target.value)} disabled={locked || !votesEnabled}
          />
          <Field.HelperText>Research on door-to-door turnout suggests about 15. Allowed: 5–100.</Field.HelperText>
        </Field.Root>
        <VStack align="stretch" gap={1}>
          <Text asChild fontSize="sm" fontWeight="medium">
            <label htmlFor="impact-contest">Compare against a past race</label>
          </Text>
          <NativeSelect.Root disabled={locked || noContests || contests.isLoading} maxW="32rem">
            <NativeSelect.Field id="impact-contest" value={contest} onChange={e => setContest(e.target.value)} minH="44px">
              <option value="">None</option>
              {list.map(c => (
                <option key={contestKey(c.office, c.year)} value={contestKey(c.office, c.year)}>
                  {`${c.year} ${OFFICE_LABEL[c.office] ?? c.office} — decided by ${formatVotes(c.marginVotes)} votes in your turf area`}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {noContests && (
            <Text fontSize="sm" color="muted">No election results are loaded for your turf area yet.</Text>
          )}
          {contests.error && (
            <Text fontSize="sm" color="danger.fg">
              {extractApiMessage(contests.error, "Couldn't load past races.")}
            </Text>
          )}
        </VStack>
        {loadError && (
          <Text fontSize="sm" color="danger.fg">
            {extractApiMessage(loadError, "Couldn't load the impact settings.")}
          </Text>
        )}
        {formError && <Text fontSize="sm" color="danger.fg">{formError}</Text>}
        <HStack justify="end">
          <Button colorPalette="brand" minH="44px" onClick={onSave} loading={save.isPending} disabled={locked}>
            Save impact settings
          </Button>
        </HStack>
      </VStack>
    </Card>
  )
}
